<!-- Client Integration Testing Component -->
<div class="container-fluid p-4">
  <!-- Loading State -->
  <div *ngIf="loading" class="text-center py-5">
    <div class="spinner-border text-primary" role="status">
      <span class="visually-hidden">Loading testing capabilities...</span>
    </div>
    <p class="mt-2 text-muted">Checking client credentials and testing capabilities...</p>
    <small class="text-muted">This may take a moment while we verify your client configuration.</small>
  </div>

  <!-- Header -->
  <div class="card shadow-sm mb-4" *ngIf="!loading || capabilities">
    <div class="card-body">
      <div class="d-flex justify-content-between align-items-start">
        <div>
          <h5 class="mb-1">
            <i class="bi bi-play-circle me-2"></i>
            Integration Testing - {{client?.clientName || 'Unknown Client'}}
          </h5>
          <div class="d-flex gap-2 align-items-center mt-2">
            <span class="badge" 
                  [class.bg-success]="capabilities?.hasCredentials" 
                  [class.bg-warning]="!capabilities?.hasCredentials">
              <i class="bi" [class.bi-shield-check]="capabilities?.hasCredentials" [class.bi-shield-exclamation]="!capabilities?.hasCredentials"></i>
              {{capabilities?.hasCredentials ? 'Testing Available' : 'Credentials Required'}}
            </span>
            <small class="text-muted">Client ID: {{clientId}}</small>
          </div>
        </div>
        <div class="d-flex gap-2">
          <button class="btn btn-outline-secondary btn-sm" (click)="refreshData()" [disabled]="loading">
            <i class="bi bi-arrow-clockwise me-1"></i>
            Refresh
          </button>
          <!-- Debug button (development mode only) -->
          <button class="btn btn-outline-info btn-sm" (click)="showDebugInfo()" *ngIf="!environment?.isProduction" title="Show debug information in console">
            <i class="bi bi-bug me-1"></i>
            Debug
          </button>
        </div>
      </div>
    </div>

    <!-- Enhanced Testing Status Message -->
    <div class="alert" 
         [class.alert-warning]="!capabilities?.hasCredentials" 
         [class.alert-info]="capabilities?.hasCredentials && !capabilities?.message?.includes('required')" 
         [class.alert-danger]="capabilities?.message?.includes('Error') || capabilities?.message?.includes('failed')"
         [class.alert-success]="capabilities?.hasCredentials && capabilities?.message?.includes('available')"
         *ngIf="capabilities?.message">
      <i class="bi me-2" 
         [class.bi-exclamation-triangle-fill]="!capabilities?.hasCredentials || capabilities?.message?.includes('required')" 
         [class.bi-info-circle-fill]="capabilities?.hasCredentials && !capabilities?.message?.includes('Error')"
         [class.bi-x-circle-fill]="capabilities?.message?.includes('Error') || capabilities?.message?.includes('failed')"
         [class.bi-check-circle-fill]="capabilities?.hasCredentials && capabilities?.message?.includes('available')"></i>
      <strong *ngIf="!capabilities?.hasCredentials">Testing Not Available:</strong>
      <strong *ngIf="capabilities?.hasCredentials && capabilities?.message?.includes('available')">Testing Ready:</strong>
      <strong *ngIf="capabilities?.message?.includes('Error')">Error:</strong>
      {{capabilities?.message}}
      
      <!-- Additional help for credential issues -->
      <div class="mt-2" *ngIf="!capabilities?.hasCredentials && !capabilities?.message?.includes('Error')">
        <small class="text-muted d-block mb-2">
          <i class="bi bi-info-circle me-1"></i>
          Go to the <strong>Credentials</strong> tab to generate API credentials, or configure integration credentials for this client.
        </small>
        <button class="btn btn-outline-primary btn-sm" (click)="reloadCredentials()" [disabled]="loading">
          <i class="bi bi-arrow-clockwise me-1"></i>
          Recheck Credentials
        </button>
      </div>
    </div>

    <!-- No Credentials Available - Show Disabled State -->
    <div class="row g-3 mb-4" *ngIf="capabilities && !capabilities.hasCredentials">
      <div class="col-12">
        <div class="card border-warning bg-light">
          <div class="card-body text-center py-4">
            <i class="bi bi-shield-exclamation text-warning fs-1 mb-3"></i>
            <h5 class="text-muted mb-3">Testing Features Available After Credential Setup</h5>
            <p class="text-muted mb-3">The following testing capabilities will be enabled once you configure credentials for this client:</p>
            
            <!-- Disabled Test Buttons Preview -->
            <div class="row g-2 justify-content-center">
              <div class="col-md-3">
                <button class="btn btn-outline-primary btn-sm" disabled>
                  <i class="bi bi-plus-circle me-1"></i>
                  Create Order Test
                </button>
              </div>
              <div class="col-md-3">
                <button class="btn btn-outline-warning btn-sm" disabled>
                  <i class="bi bi-pencil-square me-1"></i>
                  Update Order Test
                </button>
              </div>
              <div class="col-md-3">
                <button class="btn btn-outline-success btn-sm" disabled>
                  <i class="bi bi-check2-all me-1"></i>
                  Full Workflow Test
                </button>
              </div>
              <div class="col-md-3">
                <button class="btn btn-outline-info btn-sm" disabled>
                  <i class="bi bi-gear me-1"></i>
                  Bulk Test
                </button>
              </div>
            </div>
            
            <div class="mt-3">
              <small class="text-muted">Go to the <strong>Credentials</strong> tab to generate API credentials or configure integration settings.</small>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Quick Test Actions - Enabled State -->
    <div class="row g-3 mb-4" *ngIf="capabilities && capabilities.hasCredentials">
      <div class="col-md-4" *ngIf="capabilities.canTestCreate">
        <div class="card h-100 border-primary">
          <div class="card-body text-center">
            <i class="bi bi-plus-circle text-primary fs-1 mb-3"></i>
            <h6 class="card-title">Quick Create Test</h6>
            <p class="card-text text-muted small">Test order creation with default values</p>
            <button class="btn btn-primary btn-sm" 
                    (click)="quickTestCreate()" 
                    [disabled]="testInProgress || loading">
              <span *ngIf="testInProgress" class="spinner-border spinner-border-sm me-1"></span>
              <i class="bi bi-play-fill me-1" *ngIf="!testInProgress"></i>
              {{testInProgress ? 'Testing...' : 'Run Test'}}
            </button>
          </div>
        </div>
      </div>


      <div class="col-md-4" *ngIf="capabilities.canTestUpdate">
        <div class="card h-100 border-warning">
          <div class="card-body text-center">
            <i class="bi bi-pencil-square text-warning fs-1 mb-3"></i>
            <h6 class="card-title">Quick Update Test</h6>
            <p class="card-text text-muted small">Test order update with sample data</p>
            <button class="btn btn-warning btn-sm" 
                    (click)="quickTestUpdate()" 
                    [disabled]="testInProgress || loading">
              <span *ngIf="testInProgress" class="spinner-border spinner-border-sm me-1"></span>
              <i class="bi bi-play-fill me-1" *ngIf="!testInProgress"></i>
              {{testInProgress ? 'Testing...' : 'Run Test'}}
            </button>
          </div>
        </div>
      </div>

      <div class="col-md-4" *ngIf="capabilities.canTestFullWorkflow">
        <div class="card h-100 border-success">
          <div class="card-body text-center">
            <i class="bi bi-check2-all text-success fs-1 mb-3"></i>
            <h6 class="card-title">Full Workflow Test</h6>
            <p class="card-text text-muted small">Test complete order lifecycle</p>
            <button class="btn btn-success btn-sm" 
                    (click)="quickTestWorkflow()" 
                    [disabled]="testInProgress || loading">
              <span *ngIf="testInProgress" class="spinner-border spinner-border-sm me-1"></span>
              <i class="bi bi-play-fill me-1" *ngIf="!testInProgress"></i>
              {{testInProgress ? 'Testing...' : 'Run Workflow'}}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Bulk Test Configuration Section -->
    <div class="card mt-4" *ngIf="capabilities && capabilities.hasCredentials && capabilities.canTestCreate">
      <div class="card-header">
        <h6 class="mb-0">
          <i class="bi bi-gear text-info me-2"></i>
          Bulk Test Configuration
        </h6>
        <small class="text-muted">Configure and execute bulk order creation tests</small>
      </div>
      <div class="card-body">
        <form [formGroup]="bulkTestForm" (ngSubmit)="executeBulkTest()">
          <div class="row g-3">
            <div class="col-md-4">
              <label for="numberOfOrders" class="form-label">
                <i class="bi bi-hash me-1"></i>
                Number of Orders
              </label>
              <input
                type="number"
                id="numberOfOrders"
                class="form-control"
                formControlName="numberOfOrders"
                min="1"
                max="10"
                placeholder="5"
                [class.is-invalid]="bulkTestForm.get('numberOfOrders')?.invalid && bulkTestForm.get('numberOfOrders')?.touched">
              <div class="form-text">Range: 1-10 orders</div>
              <div class="invalid-feedback" *ngIf="bulkTestForm.get('numberOfOrders')?.invalid && bulkTestForm.get('numberOfOrders')?.touched">
                <span *ngIf="bulkTestForm.get('numberOfOrders')?.errors?.['required']">Number of orders is required</span>
                <span *ngIf="bulkTestForm.get('numberOfOrders')?.errors?.['min']">Minimum 1 order required</span>
                <span *ngIf="bulkTestForm.get('numberOfOrders')?.errors?.['max']">Maximum 10 orders allowed</span>
              </div>
            </div>

            <div class="col-md-4">
              <label for="numberOfParcelsPerOrder" class="form-label">
                <i class="bi bi-box me-1"></i>
                Parcels Per Order
              </label>
              <input
                type="number"
                id="numberOfParcelsPerOrder"
                class="form-control"
                formControlName="numberOfParcelsPerOrder"
                min="1"
                max="20"
                placeholder="2"
                [class.is-invalid]="bulkTestForm.get('numberOfParcelsPerOrder')?.invalid && bulkTestForm.get('numberOfParcelsPerOrder')?.touched">
              <div class="form-text">Range: 1-20 parcels</div>
              <div class="invalid-feedback" *ngIf="bulkTestForm.get('numberOfParcelsPerOrder')?.invalid && bulkTestForm.get('numberOfParcelsPerOrder')?.touched">
                <span *ngIf="bulkTestForm.get('numberOfParcelsPerOrder')?.errors?.['required']">Number of parcels is required</span>
                <span *ngIf="bulkTestForm.get('numberOfParcelsPerOrder')?.errors?.['min']">Minimum 1 parcel required</span>
                <span *ngIf="bulkTestForm.get('numberOfParcelsPerOrder')?.errors?.['max']">Maximum 20 parcels allowed</span>
              </div>
            </div>

            <div class="col-md-4">
              <label for="testPrefix" class="form-label">
                <i class="bi bi-tag me-1"></i>
                Test Prefix
              </label>
              <input
                type="text"
                id="testPrefix"
                class="form-control"
                formControlName="testPrefix"
                maxlength="10"
                placeholder="BULK"
                [class.is-invalid]="bulkTestForm.get('testPrefix')?.invalid && bulkTestForm.get('testPrefix')?.touched">
              <div class="form-text">Max 10 characters</div>
              <div class="invalid-feedback" *ngIf="bulkTestForm.get('testPrefix')?.invalid && bulkTestForm.get('testPrefix')?.touched">
                <span *ngIf="bulkTestForm.get('testPrefix')?.errors?.['required']">Test prefix is required</span>
                <span *ngIf="bulkTestForm.get('testPrefix')?.errors?.['maxlength']">Maximum 10 characters allowed</span>
              </div>
            </div>
          </div>

          <div class="row mt-3">
            <div class="col-12">
              <div class="d-flex justify-content-between align-items-center">
                <div class="text-muted small">
                  <i class="bi bi-info-circle me-1"></i>
                  This will create <strong>{{bulkTestForm.get('numberOfOrders')?.value || 0}}</strong> test orders, 
                  each with <strong>{{bulkTestForm.get('numberOfParcelsPerOrder')?.value || 0}}</strong> parcels
                </div>
                <button
                  type="submit"
                  class="btn btn-info"
                  [disabled]="bulkTestForm.invalid || testInProgress || loading">
                  <span *ngIf="testInProgress || loading" class="spinner-border spinner-border-sm me-2"></span>
                  <i class="bi bi-play-fill me-1" *ngIf="!testInProgress && !loading"></i>
                  {{testInProgress || loading ? 'Running Test...' : 'Execute Bulk Test'}}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>

    <!-- Test Results Table -->
    <div class="card mt-4" *ngIf="testResults.length > 0">
      <div class="card-header">
        <h6 class="mb-0">
          <i class="bi bi-list-check me-2"></i>
          Recent Test Results
        </h6>
      </div>
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover mb-0">
            <thead class="table-light">
              <tr>
                <th>Test Type</th>
                <th>Status</th>
                <th>Executed At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let result of testResults.slice(0, 10)">
                <td>{{result.testType}}</td>
                <td>
                  <span class="badge" 
                        [class.bg-success]="result.success" 
                        [class.bg-danger]="!result.success">
                    {{result.success ? 'Success' : 'Failed'}}
                  </span>
                </td>
                <td>{{result.executedAt | date:'short'}}</td>
                <td>
                  <button class="btn btn-outline-primary btn-sm" 
                          (click)="viewTestDetails(result)">
                    <i class="bi bi-eye me-1"></i>
                    View Details
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Available Tests Overview -->
    <div class="card mt-4" *ngIf="capabilities">
      <div class="card-header">
        <h6 class="mb-0">
          <i class="bi" [class.bi-check-circle]="capabilities.hasCredentials" [class.bi-x-circle]="!capabilities.hasCredentials" me-2></i>
          {{capabilities.hasCredentials ? 'Available Test Endpoints' : 'Test Endpoints (Requires Credentials)'}}
        </h6>
        <small class="text-muted" *ngIf="!capabilities.hasCredentials">
          These tests will become available after setting up client credentials
        </small>
      </div>
      <div class="card-body">
        <div class="row g-2">
          <div class="col-md-6">
            <div class="d-flex align-items-center">
              <i [class]="capabilities.canTestCreate ? 'bi bi-check-circle text-success' : 'bi bi-x-circle text-muted'"></i>
              <span class="ms-2">Create Order Test</span>
            </div>
          </div>
          <div class="col-md-6">
            <div class="d-flex align-items-center">
              <i [class]="capabilities.canTestUpdate ? 'bi bi-check-circle text-success' : 'bi bi-x-circle text-muted'"></i>
              <span class="ms-2">Update Order Test</span>
            </div>
          </div>
          <div class="col-md-6">
            <div class="d-flex align-items-center">
              <i [class]="capabilities.canTestCancel ? 'bi bi-check-circle text-success' : 'bi bi-x-circle text-muted'"></i>
              <span class="ms-2">Cancel Order Test</span>
            </div>
          </div>
          <div class="col-md-6">
            <div class="d-flex align-items-center">
              <i [class]="capabilities.canTestPushInfo ? 'bi bi-check-circle text-success' : 'bi bi-x-circle text-muted'"></i>
              <span class="ms-2">Push Info Test</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Fallback Message When No Capabilities Data -->
    <div class="card mt-4" *ngIf="!loading && !capabilities">
      <div class="card-body text-center py-4">
        <i class="bi bi-question-circle text-muted fs-1 mb-3"></i>
        <h5 class="text-muted mb-3">Unable to Load Testing Information</h5>
        <p class="text-muted mb-3">There was an issue loading the testing capabilities for this client.</p>
        <button class="btn btn-outline-primary" (click)="refreshData()" [disabled]="loading">
          <i class="bi bi-arrow-clockwise me-1"></i>
          Try Again
        </button>
      </div>
    </div>
  </div>

  <!-- Test Result Details Modal -->
  <div class="modal fade" [class.show]="showResultModal" [style.display]="showResultModal ? 'block' : 'none'" 
       tabindex="-1" *ngIf="showResultModal && selectedResult">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="bi bi-file-earmark-text me-2"></i>
            Test Result Details - {{selectedResult.testType}}
          </h5>
          <button type="button" class="btn-close" (click)="closeResultModal()"></button>
        </div>
        <div class="modal-body">
          <div class="row g-3">
            <div class="col-md-6">
              <strong>Status:</strong>
              <span class="badge ms-2" [class.bg-success]="selectedResult.success" [class.bg-danger]="!selectedResult.success">
                {{selectedResult.success ? 'Success' : 'Failed'}}
              </span>
            </div>
            <div class="col-md-6">
              <strong>Executed At:</strong>
              <span class="ms-2">{{selectedResult.executedAt | date:'medium'}}</span>
            </div>
            <div class="col-12" *ngIf="selectedResult.response">
              <strong>Response:</strong>
              <pre class="bg-light p-3 mt-2 rounded"><code>{{selectedResult.response | json}}</code></pre>
            </div>
            <div class="col-12" *ngIf="selectedResult.error">
              <strong>Error:</strong>
              <div class="alert alert-danger mt-2">{{selectedResult.error}}</div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" (click)="closeResultModal()">
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</div>